import React, { useCallback, useEffect, useState } from 'react';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { attendanceAPI, studentAPI, batchAPI } from '../services/api';
import Modal from '../components/Modal';
import './AttendancePage.css';

function AttendancePage({
  studentId = null,
  readOnly = false,
  title = 'Attendance Management',
  subtitle = ''
}) {
  const [attendance, setAttendance] = useState([]);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    StudentID: '',
    BatchID: '',
    ClassDate: new Date().toISOString().split('T')[0],
    Status: 'Present',
  });

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const response = studentId ? await attendanceAPI.getByStudent(studentId) : await attendanceAPI.getAll();
      setAttendance(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch attendance: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchAttendance();
    if (!readOnly) {
      fetchStudents();
    }
    fetchBatches();
  }, [fetchAttendance, readOnly]);

  const fetchStudents = async () => {
    try {
      const response = await studentAPI.getAll();
      setStudents(response.data);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data);
    } catch (err) {
      console.error('Failed to fetch batches:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAttendance = attendance.filter((record) => {
    if (readOnly) {
      const batch = batches.find((item) => String(item.BatchID) === String(record.BatchID));
      return (batch?.BatchName || '').toLowerCase().includes(searchTerm.toLowerCase());
    }

    const student = students.find((s) => String(s.StudentID) === String(record.StudentID));
    const studentName = student ? `${student.FirstName} ${student.LastName}` : '';
    return studentName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingId(record.AttendanceID);
      setFormData(record);
    } else {
      setEditingId(null);
      setFormData({
        StudentID: '',
        BatchID: '',
        ClassDate: new Date().toISOString().split('T')[0],
        Status: 'Present',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await attendanceAPI.update(editingId, formData);
      } else {
        await attendanceAPI.create(formData);
      }
      await fetchAttendance();
      handleCloseModal();
      setError('');
    } catch (err) {
      setError('Failed to save attendance: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await attendanceAPI.delete(id);
        fetchAttendance();
        setError('');
      } catch (err) {
        setError('Failed to delete attendance: ' + err.message);
      }
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.StudentID === studentId);
    return student ? `${student.FirstName} ${student.LastName}` : 'N/A';
  };

  return (
    <div className="attendance-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">{title}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
          {!readOnly && (
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <FaPlus /> Mark Attendance
            </button>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder={readOnly ? 'Search by batch name...' : 'Search by student name...'}
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {loading ? (
          <div className="loading">Loading attendance...</div>
        ) : filteredAttendance.length === 0 ? (
          <div className="no-data">No attendance records found</div>
        ) : (
          <div className="table-responsive">
            <table className="responsive-table">
              <thead>
                <tr>
                  {!readOnly && <th>Student</th>}
                  <th>Batch</th>
                  <th>Date</th>
                  <th>Status</th>
                  {!readOnly && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredAttendance.map((record) => (
                  <tr key={record.AttendanceID}>
                    {!readOnly && <td data-label="Student">{getStudentName(record.StudentID)}</td>}
                    <td data-label="Batch">{batches.find((batch) => String(batch.BatchID) === String(record.BatchID))?.BatchName || 'N/A'}</td>
                    <td data-label="Date">{record.ClassDate}</td>
                    <td data-label="Status">
                      <span className={`status-badge ${record.Status.toLowerCase()}`}>
                        {record.Status}
                      </span>
                    </td>
                    {!readOnly && (
                      <td data-label="Actions" className="action-buttons">
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => handleOpenModal(record)}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(record.AttendanceID)}
                        >
                          <FaTrash /> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!readOnly && (
        <Modal
          isOpen={isModalOpen}
          title={editingId ? 'Edit Attendance' : 'Mark Attendance'}
          onClose={handleCloseModal}
        >
          <form onSubmit={handleSubmit} className="attendance-form">
          <div className="form-group">
            <label>Student *</label>
            <select
              name="StudentID"
              value={formData.StudentID}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Student</option>
              {students.map((student) => (
                <option key={student.StudentID} value={student.StudentID}>
                  {student.FirstName} {student.LastName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Batch *</label>
            <select
              name="BatchID"
              value={formData.BatchID}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Batch</option>
              {batches.map((batch) => (
                <option key={batch.BatchID} value={batch.BatchID}>
                  {batch.BatchName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              name="ClassDate"
              value={formData.ClassDate}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              name="Status"
              value={formData.Status}
              onChange={handleInputChange}
            >
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Leave">Leave</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Mark Present'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default AttendancePage;
